import type { HostedEvent } from "@hobbyshopify/backend-client";
import { useObserver, useSubtree } from "subtree.js/react";
import { LoadFailed, Loading } from "../../components/LoadState";
import { ShareButton } from "../../components/ShareButton";
import { owedBadge, showDates, showStatus, venueLine } from "../../core/primitives/hosted-event";
import type { ShareTarget } from "../../core/primitives/share-links";
import { CapabilityRequestInline } from "../../sections/capability-request/capability-request";
import { EventsActions, EventsState } from "./events-model";
import { VendorShowsSection } from "./sections/vendor-shows/vendor-shows-section";

function ShowRow({ event, mayOpen, share }: { event: HostedEvent; mayOpen: boolean; share: ShareTarget | undefined }) {
  const actions = useSubtree(EventsActions);
  const status = showStatus(event);
  const owed = event.refundsOwed.length;
  const row = (
    <div className="min-w-0 space-y-1">
      <div className="flex items-center gap-2">
        <span className={`badge shrink-0 ${status.cls}`}>{status.label}</span>
        {owed > 0 && <span className="badge badge-danger shrink-0">{owedBadge(owed)}</span>}
        <span className="truncate font-medium">{event.title}</span>
      </div>
      <p className="text-sm opacity-60">{showDates(event)}</p>
      <p className="truncate text-sm opacity-60">{venueLine(event.place)}</p>
    </div>
  );
  return (
    <li className="panel flex items-center gap-2 p-3 hover:border-white/30">
      {mayOpen ? (
        <button type="button" className="block min-w-0 flex-1 text-left" onClick={() => actions.open(event.id)}>
          {row}
        </button>
      ) : (
        <div className="min-w-0 flex-1">{row}</div>
      )}
      {share && (
        <ShareButton
          target={share}
          heading="Share this show"
          note="Vendors can buy a table and visitors add it to their calendar from this page."
          iconOnly
          className="btn-ghost btn-sm shrink-0"
        />
      )}
    </li>
  );
}

function ShowList({
  title,
  events,
  mayOpen,
  shares,
}: {
  title: string;
  events: readonly HostedEvent[];
  mayOpen: boolean;
  shares: ReadonlyMap<string, ShareTarget>;
}) {
  if (events.length === 0) return null;
  return (
    <section className="space-y-2">
      <h2 className="eyebrow">{title}</h2>
      <ul className="space-y-2">
        {events.map((event) => (
          <ShowRow key={event.id} event={event} mayOpen={mayOpen} share={shares.get(event.id)} />
        ))}
      </ul>
    </section>
  );
}

export function EventsPage() {
  const state = useSubtree(EventsState);
  const actions = useSubtree(EventsActions);
  const observer = useObserver();
  const status = observer.watch(state.status);
  const canHost = observer.watch(state.canHost);
  const mayManage = observer.watch(state.mayManage);
  const upcoming = observer.watch(state.upcoming);
  const past = observer.watch(state.past);
  const shares = observer.watch(state.shares);

  const mayHost = canHost && mayManage;
  const none = status.kind === "loaded" && upcoming.length === 0 && past.length === 0;

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-4">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Shows</h1>
          {canHost && (
            <p className="text-sm opacity-60">
              Shows your shop hosts, and other shops' shows where you can take a table.
            </p>
          )}
        </div>
        {mayHost && (
          <button type="button" className="btn-primary" onClick={actions.create}>
            Host a show
          </button>
        )}
      </header>

      {status.kind === "error" && (
        <LoadFailed title="Couldn't load your shows." message={status.message} onRetry={actions.retry} />
      )}
      {status.kind === "loading" && upcoming.length === 0 && <Loading label="Loading the shows…" />}

      {none && mayHost && (
        <div className="panel p-6 text-center">
          <p className="font-medium">No shows yet</p>
          <p className="mt-1 text-sm opacity-60">
            Add your first show, then its tables. Nobody else sees it until you publish it.
          </p>
        </div>
      )}

      <ShowList title="Coming up" events={upcoming} mayOpen={mayManage} shares={shares} />
      <ShowList title="Over" events={past} mayOpen={mayManage} shares={shares} />

      <VendorShowsSection />

      {status.kind === "loaded" && !canHost && <CapabilityRequestInline prompt="Do you want to host your own show?" />}
    </div>
  );
}
